import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Form, Modal } from '@syneto/compass-react';
import { OVERRIDE_REASON_MAX_LENGTH, REMOTE_SUPPORT_OVERRIDE_DAYS, validateOverrideReason } from '../../../../helpers/remoteSupport';

/*
 * Asks for the reason the override is recorded with. The reason is validated here with the API's two
 * rules (not blank, at most 1,000 characters), so a refused save is not the first feedback. `onConfirm`
 * returns a promise: while it is pending the dialog is locked, and when it rejects the dialog stays
 * open with the message, so the reason can be resubmitted. It closes only when the save went through.
 */
const AllowRemoteSupportModal = ({ serialNumber, onHide, onConfirm }) => {
	const [reason, setReason] = useState('');
	const [touched, setTouched] = useState(false);
	const [saving, setSaving] = useState(false);
	const [saveError, setSaveError] = useState(null);

	const validationError = validateOverrideReason(reason);

	const handleReasonChange = (event) => {
		setReason(event.target.value);
		setSaveError(null);
	};

	const handleConfirm = async () => {
		setTouched(true);

		if (validationError || saving) {
			return;
		}

		setSaving(true);
		setSaveError(null);

		try {
			await onConfirm(reason.trim());
		} catch (error) {
			setSaveError(error?.message || 'Remote support could not be allowed.');
			setSaving(false);
		}
	};

	const handleHide = () => {
		if (!saving) {
			onHide();
		}
	};

	return (
		<Modal
			show={true}
			onHide={handleHide}
			title={`Allow remote support for ${REMOTE_SUPPORT_OVERRIDE_DAYS} days`}
			content={
				<div>
					<p>
						The support tunnel of {serialNumber ? <span className="text-value">{serialNumber}</span> : 'this machine'} will
						be allowed to open for the next {REMOTE_SUPPORT_OVERRIDE_DAYS} days, whatever its maintenance says.
						This cannot be cancelled early.
					</p>

					<Form.Group className="mb-3" controlId="allow-remote-support-reason">
						<Form.Label>Reason*</Form.Label>
						<Form.Control
							as="textarea"
							rows={3}
							name="reason"
							value={reason}
							maxLength={OVERRIDE_REASON_MAX_LENGTH}
							disabled={saving}
							isInvalid={touched && Boolean(validationError)}
							onChange={handleReasonChange}
							onBlur={() => { setTouched(true); }}
							autoFocus
						/>
						<Form.Control.Feedback type="invalid">{validationError}</Form.Control.Feedback>
						<Form.Text className="text-muted">
							{reason.length.toLocaleString('en-US')}/{OVERRIDE_REASON_MAX_LENGTH.toLocaleString('en-US')} characters.
							Recorded with your account.
						</Form.Text>
					</Form.Group>

					{saveError && <Alert variant="danger" className="mb-0">{saveError}</Alert>}
				</div>
			}
			actions={{
				primary: {
					label: saving ? 'Allowing…' : 'Allow remote support',
					onClick: handleConfirm,
					disabled: saving || (touched && Boolean(validationError))
				},
				secondary: {
					label: 'Cancel',
					onClick: handleHide,
					disabled: saving
				}
			}}
		/>
	);
};

AllowRemoteSupportModal.propTypes = {
	serialNumber: PropTypes.string,
	onHide: PropTypes.func.isRequired,
	onConfirm: PropTypes.func.isRequired
};

export default AllowRemoteSupportModal;
